import React, {useEffect, useMemo, useState} from 'react';
import {useProject} from "../../providers/ClientProjectProvider";
import {Alert, Badge, Button, Checkbox, Collapse, Divider, Modal, Radio, Select, Space} from "antd";
import ProjectFormProvider, {useProjectForm} from "../../providers/ProjectFormProvider";
import {useAuth} from "../../providers/AuthProvider";
import {useAPI} from "../../config/API";
import SpinLoader from "../../components/SpinLoader";
import TalentPoolProvider from "../../providers/TalentPoolProvider";
import {useQueries, useQueryClient} from "@tanstack/react-query";
import Project from "../../models/Project";
import LoadingSpinner from "../../components/LoadingSpinner";
import {usePosition} from "../../providers/ProjectPositionProvider";
import ErrorBanner from "../../components/ErrorBanner";
import {getPositionMatchingTalentCount} from "../../requests/BYORequests";

const ProjectVisibilitySettingsInfo = (props) => {

    const {auth} = useAuth();

    const {project, isProjectUpdating} = useProject();
    const {position} = usePosition();

    const queryClient = useQueryClient()
    const [showSettingsModal, setShowSettingsModal] = useState(false);

    const {
        data: positionMatchesTotals,
        isLoading: positionMatchesTotalsLoading,
        error: positionMatchesTotalsError,
    } = useAPI(['getPositionMatchingTalentCount', {projectId: project?.id, positionId: position.id, visibilitySettings: project?.visibilitySettings()}],
        {
            enabled: !!project && !!position?.id,
            onSuccess: async (resp) => {
                // if (resp.matches_total <= 10 && resp?.possible_matches_total !== resp.matches_total && resp?.possible_matches_total > 10) {
                //     if (!showSettingsModal) {
                //
                //         TB.alert('tip',"Increase your talent matches for this role", `Based on your project visibility settings, there are <b><span class="text-primary">${resp.matches_total}</span> out of <span class="text-primary">${resp.possible_matches_total}</span> possible talent matches</b> for this role: <b>${position.title}</b>. To expand your results, consider updating the project visibility settings.`,[
                //             {title: "Update Settings Now", click: async () => {
                //                     if (!showSettingsModal) {
                //                         await TB.wait(500);
                //                         try {
                //                             return setShowSettingsModal(true);
                //                         }catch (e) {
                //                             console.warn("Show settings error", e)
                //                         }
                //                     }
                //                 }}, {title: "Dismiss"}
                //         ], {preferenceKey: `bidding.position_${position.id}_talent_matches`})
                //     }
                //
                // }
            }
        }
    )

    const locationMileSetting = useMemo(() => {

        if (!project) {
            return 200
        }
        let setting = laravel.project_visibility_location_settings?.[project?.visibility_setting_location];

        if (setting && setting.includes('Within')) {
            return parseInt(setting.replace(/^\D+/g, ''))
        }
        return 200
    }, [project, project?.visibility_setting_location, project?.visibility_setting_location_mixed]);


    if (!project) {
        return null;
    }
    return (
        <div className="flex flex-col items-stretch bg-slate-100 px-3 rounded-md py-2.5 mx-2">
            <div className="flex flex-row items-center flex-nowrap justify-between">
                <p className="text-base text-primary font-medium flex-grow line-clamp-1">
                    Talent Visibility Settings
                </p>
                {project.canEditScope() && (
                    <Button type="primary" ghost size="small" disabled={isProjectUpdating || positionMatchesTotalsLoading} onClick={e => setShowSettingsModal(true)}>
                        Edit
                    </Button>
                )}
            </div>
            <div className="flex flex-col gap-3 items-stretch mt-4">
                <div className="flex flex-row items-center gap-2">
                    <div className="text-center">
                        <i className={TB.mapClass("w-9 fa-2x", {
                            "fas fa-check-square": project.posts_on_talent_job_board,
                            "far fa-minus-square": !project.posts_on_talent_job_board,
                        })}/>
                    </div>
                    <div className="flex flex-col">
                        <p className="text-sm font-medium">
                            Posted to Job Board
                        </p>
                    </div>
                </div>

                <Divider className="my-0"/>
                <div className="pointer-events-none flex flex-col gap-3">
                    <p className="text-sm font-semibold">Talent Location</p>
                    {(!project.visibility_setting_location_mixed || project.visibility_setting_location_mixed === TB.locationGlobalSetting()) ? (
                        <div className="flex flex-row items-center gap-2">

                            <div className="text-center">
                                <i className="w-9 fas fa-globe-americas fa-2x"/>
                            </div>

                            <div className="flex flex-col gap-1.5">
                                <p className="whitespace-no-wrap text-sm font-medium">
                                    Worldwide
                                </p>

                            </div>
                        </div>
                    ) : (
                        project.visibility_setting_location_mixed === TB.locationRegionalSetting() ? (
                            <div className="flex flex-row items-center gap-2">
                                <div className="text-center">
                                    <i className="w-9 fas fa-flag-usa fa-2x"/>
                                </div>
                                <div className="flex flex-col">
                                    <p className="whitespace-no-wrap text-sm font-medium">
                                        U.S. Only
                                    </p>
                                </div>
                            </div>
                        ) : project.visibility_setting_location_mixed === TB.locationProximitySetting() && (
                            <div className="flex flex-col gap-2.5">
                                <div className="flex flex-row items-center gap-2.5">
                                    <div className="text-center">
                                        <i className="w-9 fas fa-map-marked-alt fa-2x"/>
                                    </div>
                                    <div className="flex flex-col">
                                        <p className="text-sm font-medium">
                                            By Proximity to Location
                                        </p>
                                    </div>

                                </div>
                                <div className="flex flex-col items-right justify-start gap-1.5">
                                    <div className="flex flex-row flex-wrap gap-1 items-center">
                                        <p className="m-0 text-sm">
                                            Talent Lives Within&nbsp;
                                        </p>
                                        <span className="border border-black px-1 py-0.5 text-sm font-medium">
                                            {locationMileSetting} miles
                                        </span>
                                    </div>

                                    <div className="flex flex-row flex-wrap gap-1 items-center">
                                        <p className="m-0 whitespace-no-wrap text-sm">
                                            of Project Location:
                                        </p>
                                        <Badge
                                            color={TB.config('color.secondary')}
                                            showZero
                                            className="whitespace-no-wrap text-xs"
                                            count={TB.truncate(project.location?.full, {length: 50})}
                                        />
                                    </div>


                                </div>
                            </div>
                        )
                    )}
                </div>
                <Divider className="my-0"/>

                <div className="pointer-events-none flex flex-col gap-3">
                    <p className="text-sm font-semibold">Talent Pools</p>
                    <div className={TB.mapClass("flex flex-row gap-2", {
                        "items-start": project.hasVisibilityPoolRestrictions(),
                        "items-center": !project.hasVisibilityPoolRestrictions(),
                    })}>
                        <div className={TB.mapClass("text-center", {
                            "flex flex-col items-center justify-center h-[40px]": project.hasVisibilityPoolRestrictions()
                        })}>
                            <i className={TB.mapClass("w-9 fa-2x", {
                                "fas fa-check-square": project.hasVisibilityPoolRestrictions(),
                                "far fa-minus-square": !project.hasVisibilityPoolRestrictions(),
                            })}/>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <p className="text-sm font-medium">
                                {project.hasVisibilityPoolRestrictions() ? "Only allow talent proposals from selected talent pools" : "Open to All Matching Talent"}
                            </p>
                            {project.hasVisibilityPoolRestrictions() && (
                                <p className="text-sm text-primary font-medium">
                                    {TB.pluralize('pool',project.totalVisibilityPoolRestrictions(), true)} selected
                                </p>
                            )}

                        </div>
                    </div>
                </div>
                <Divider className="my-0"/>
                {!positionMatchesTotalsError && (
                    <div className="flex flex-row items-center justify-start gap-1.5">
                        <span className="text-sm font-semibold">
                            {positionMatchesTotalsLoading ? (
                                <span className="mr-1.5">
                                    <LoadingSpinner size="sm"/>
                                </span>
                            ) : (
                                <span className="text-primary">{positionMatchesTotals.matches_total} </span>
                            )}
                            {positionMatchesTotals?.matches_total !== positionMatchesTotals?.possible_matches_total && positionMatchesTotals.possible_matches_total > 0 && (
                                <React.Fragment>
                                    out of
                                    {positionMatchesTotalsLoading ? (
                                        <span className="mx-1.5">
                                            <LoadingSpinner size="sm"/>
                                        </span>
                                    ) : (
                                        <span className="text-primary"> {positionMatchesTotals.possible_matches_total} </span>
                                    )}
                                </React.Fragment>
                            )}
                            possible talent matches for this position
                        </span>
                    </div>
                )}
            </div>
            <Modal
                open={showSettingsModal}
                footer={null}
                zIndex={199}
                title={null}
                closeable={true}
                maskClosable={true}
                style={{top: 20}}
                wrapClassName="flex flex-col items-stretch px-6"
                destroyOnClose={true}
                onCancel={e => setShowSettingsModal(false)}
                afterClose={() => setShowSettingsModal(false)}
                width="auto"
            >

                <ProjectFormProvider>
                    <TalentPoolProvider>
                        <ProjectVisibilitySettings onUpdate={async () => {
                            await setShowSettingsModal(false);
                            TB.toast("Talent Visibility Settings Updated");
                            await queryClient.invalidateQueries({
                                predicate: ({queryKey}) => {
                                    return queryKey[0] === 'positionMatches' || queryKey[0] === 'getPositionMatchingTalentCount';
                                }
                            })

                        }} showActions={true} onClose={() => setShowSettingsModal(false)} />
                    </TalentPoolProvider>
                </ProjectFormProvider>
            </Modal>

        </div>
    )
};

export default ProjectVisibilitySettingsInfo;

const ProjectVisibilitySettings = (props) => {

    const {
        formModel: project,
        project: originalProject,
        isDisabled,
        onFormChange,
        onFormSubmit,
        isProjectUpdating
    } = useProjectForm();

    const queryClient = useQueryClient()
    const [isSelectingTalentPoolsEnabled, setIsSelectingTalentPoolsEnabled] = useState(false)
    const {auth, client_org_regions} = useAuth();

    useEffect(() => {
        if (originalProject) {
            setIsSelectingTalentPoolsEnabled(originalProject.hasVisibilityPoolRestrictions())
        }
    }, [originalProject]);

    //
    // ALL TALENT POOLS
    //
    const {
        data: availableTalentPools,
        isLoading,
        error,
        refetch
    } = useAPI(['fetchTalentPools', {clientId: auth.clientOrg?.()?.id}], {
        // enabled: !!project,
        refetchOnMount: true,
        onSuccess: async data => {
            // console.log(data, 'DATA ON SUCESSSS')
        },
    })

    const otherProjectPools = useMemo(() => {
        if (isLoading || !project?.visible_talent_pools?.length) {
            return []
        }
        let poolIds = TB.map(availableTalentPools, 'id')
        return TB.filter(project.visible_talent_pools, pool => !poolIds.includes(pool.id))
    }, [project, availableTalentPools, isLoading]);

    const groupedPools = useMemo(() => {
        if (isLoading || !project || !availableTalentPools) {
            return [];
        }
        let allPools = availableTalentPools;
        if (otherProjectPools?.length) {
            allPools = TB.uniqBy([...otherProjectPools, ...availableTalentPools], 'id');
        }
        let orderedPools =  TB.orderBy(allPools, [pool => !!TB.find(otherProjectPools, ['id', pool.id]), 'id'], ['asc','asc']);

        let grouped = TB.groupBy(orderedPools, item => {
            if (item.is_default) return 'general';
            if (item.is_personal) {

                if (auth?.canManageClients() && item.owner?.id !== auth?.id) {
                    return "client_users"
                }
                return 'personal'
            }
            return 'company'
        });
        if (auth?.canManageClients() && grouped?.client_users?.length) {
            grouped.client_users = TB.orderBy(grouped.client_users, 'owner.id', 'asc');
        }

        grouped = TB.map(grouped, (pools, key) => {
            let display_index = 0;
            switch (key) {
                case 'personal':
                    display_index = 1;
                    break;
                case 'company':
                    display_index = 2;
                    break;
                case 'client_users':
                    display_index = 3;
                    break;
            }

            return {
                label: TB.startCase(key),
                pools,
                key,
                display_index
            }
        })
        return TB.orderBy(grouped, 'display_index', 'asc')
    }, [project, otherProjectPools, availableTalentPools, isLoading]);



    //
    // LOCATION SETTINGS
    //
    const [PROXIMITY_LOCATION_SETTING, setPROXIMITY_LOCATION_SETTING] = useState(TB.locationProximitySetting())
    const [GLOBAL_LOCATION_SETTING, setGLOBAL_LOCATION_SETTING] = useState(TB.locationGlobalSetting());
    const [REGIONAL_LOCATION_SETTING, setREGIONAL_LOCATION_SETTING] = useState(TB.locationRegionalSetting())

    const availableRegionOptions = useMemo(() => {
        let regions = TB.orderBy(client_org_regions || [], ['display_index'], ['asc']);
        regions = TB.filter(regions, region => region.name !== 'All Other Countries')
        return TB.map(regions, region => ({label: region.name, value: region.id}))
    }, [client_org_regions])

    const locationMileSetting = useMemo(() => {

        if (!project) {
            return 200
        }
        let setting = laravel.project_visibility_location_settings?.[project.visibility_setting_location];

        if (setting && setting.includes('Within')) {
            return parseInt(setting.replace(/^\D+/g, ''))
        }
        return 200
    }, [project, project?.visibility_setting_location, project?.visibility_setting_location_mixed]);

    const [currentMileSetting, setCurrentMileSetting] = useState(false)

    useEffect(() => {
        if (!currentMileSetting && project) {
            setCurrentMileSetting(locationMileSetting);
        }
    }, [project])

    const onLocationChange = (e) => {
        console.log('radio checked', e.target.value);
        if (e.target.value <= 3) {
            onFormChange({visibility_setting_location: e.target.value});
        } else {
            onFormChange({
                visibility_setting_location: parseInt(TB.invert(laravel.project_visibility_location_settings)[`Within ${currentMileSetting} Miles`]),
                visibility_setting_location_mixed: PROXIMITY_LOCATION_SETTING
            })
        }
    };

    const locationOptions = useMemo(() => {
        return TB.map(TB.orderBy(auth?.clientOrg()?.regions || [], 'display_index', 'asc'), region => {
            return {
                label: region.name,
                value: region.id
            }
        })
    }, [auth?.clientOrg()?.regions])


    //
    // DYNAMIC TALENT MATCH COUNT PER ROLE
    //
    const positionMatchQueries = useQueries({
        enabled: !!project,
        queries: !!project ? project.positions.map((position) => {
            return {
                queryKey: ['getPositionMatchingTalentCount', {projectId: project?.id, positionId: position.id, visibilitySettings: project?.visibilitySettings()}],
                queryFn: getPositionMatchingTalentCount,
                enabled: !!project && !!position.id && !isDisabled,
                keepPreviousData: false,
                refetchOnMount: true,
                retry: false,
                select: (resp) => {
                    return {
                        title: position.title,
                        id: position.id,
                        matchCount: resp.matches_total,
                        possibleMatchCount: resp.possible_matches_total,
                        isTotalPossible: resp.matches_total === resp.possible_matches_total,
                    }
                }
            }
        }): [],
    })

    const onUpdateSettings = async () => {
        let projectProps = await project.validateVisibilitySettings()

        let selectedPoolIds = TB.map(projectProps.visible_talent_pools || [], 'id');
        let poolsNotOwnedByAuth = TB.filter(originalProject?.visible_talent_pools || [], pool => pool.is_personal && pool?.owner && pool?.owner?.id !== TB.auth()?.user()?.id)

        let didRemoveUserPersonalPool = poolsNotOwnedByAuth?.length > 0 && !!TB.find(poolsNotOwnedByAuth, pool => !selectedPoolIds.includes(pool.id))
        if (!props.skipWarnings && !originalProject.is_on_hold && originalProject.isBidding()) {
            let confirm = true;
            if (didRemoveUserPersonalPool && !TB.auth()?.user()?.canManageClients()) {
                confirm = await TB.sysAlert('byo.visible_talent_pool_permission_warning')
            }else if (projectProps.posts_on_talent_job_board) {
                confirm = await TB.sysAlert('byo.visibility_settings_update_note')
            }
            if (!confirm) {
                return;
            }
        }
        // let hasLowMatchCountForRole = TB.find(positionMatchQueries || [], query => {
        //     return query?.data?.matchCount <= 10 && query?.data?.possibleMatchCount > 10
        // })
        // if (hasLowMatchCountForRole) {
        //     await TB.wait(600);
        //     let confirmLowMatch = await TB.sysAlert('byo.low_talent_matches_warning')
        //     console.log('onUpdateSettings', confirmLowMatch);
        //
        //     if (!confirmLowMatch) {
        //         return false;
        //     }
        // }


        if (!projectProps) {
            return;
        }

        if (props.onSubmit) {
            return props.onSubmit(projectProps)
        }
        // if (confirm) {
        let response = await onFormSubmit(projectProps);
        if (response) {
            return props.onUpdate?.();
        }
        // }
    }

    const hasLowMatchCountForRole = useMemo(() => {
        return TB.find(positionMatchQueries || [], query => {
            return query?.data?.matchCount <= 10 && query?.data?.possibleMatchCount > 10
        })
    }, [TB.map(positionMatchQueries || [], 'isLoading')])

    if (!project) {
        return null;
    }

    return (
        <SpinLoader className="flex flex-col items-stretch justify-center" isLoading={isProjectUpdating}>
            <div className="flex flex-col items-stretch px-5 py-7 gap-3">
                {!props.formOnly && (
                    <div className="flex flex-row items-start justify-between overflow-visible gap-4 max-h-20">
                        <div className="flex-grow flex flex-col items-stretch gap-3">
                            <p className="text-xl font-semibold">
                                Talent Visibility Settings
                            </p>
                            <Divider className="mt-1 mb-3"/>
                        </div>

                        {/*<div className="w-3/12 flex flex-col items-stretch pl-4">*/}
                        {/*    <img alt="visibility-graphic" className="flex-1" src={'/images/search-talent-graphic.png'}/>*/}
                        {/*</div>*/}
                    </div>
                )}

                {props.instructions}

                <div className="self-center flex flex-row items-start justify-between project-visibility-settings">
                    <div className="flex-grow flex flex-col items-stretch gap-4">
                        <div className="flex flex-col items-stretch">
                            <div className="flex items-center gap-5 mb-3">

                                <p className="text-lg font-medium text-left text-[#3f434a]">
                                    Do you want to post this project on Wripple's private job board?
                                </p>
                            </div>


                            <div className="field-section flex-row">
                                <div className="field px-3.5 basis-10/12">
                                    <div className="form-group">
                                        <Checkbox checked={project.posts_on_talent_job_board} disabled={isDisabled}
                                                  onChange={e => {
                                                      onFormChange({posts_on_talent_job_board: e.target.checked});
                                                  }}>
                                            Yes (recommended)
                                        </Checkbox>
                                    </div>
                                </div>
                            </div>
                            <p className={TB.mapClass("transition-all text-xs my-2 max-w-[650px]")}>
                                Posting to the Wripple job board is recommended as it speeds up the process of getting
                                to a short-list of qualified candidates. When you opt to post a project to the Wripple
                                Job board, the entire Wripple talent community can view the project, and potential
                                candidates can immediately indicate an interest in the position.
                            </p>
                        </div>

                        <Divider className="mt-3 mb-2"/>

                        <div className="flex flex-col items-stretch">
                            <p className="text-lg font-medium text-left text-[#3f434a] mb-3">
                                Location of Talent
                            </p>
                            <Space className="items-stretch gap-4" direction="vertical">
                                <Radio.Group
                                    className="visibility-main-radio"

                                    onChange={(e) => {
                                        console.log('on radio change', e)
                                        if (e.target.value === GLOBAL_LOCATION_SETTING) {
                                            onFormChange({
                                                visibility_setting_location_regions: false,
                                                visibility_setting_location: GLOBAL_LOCATION_SETTING,
                                                visible_regions: [],
                                                visibility_setting_location_mixed: undefined,
                                            })
                                        } else if (e.target.value === REGIONAL_LOCATION_SETTING) {
                                            let defaultRegion = TB.first(availableRegionOptions);
                                            onFormChange({
                                                visibility_setting_location_regions: true,
                                                visibility_setting_location: REGIONAL_LOCATION_SETTING,
                                                visible_regions: [defaultRegion?.value],
                                                visibility_setting_location_mixed: undefined,
                                            })
                                            console.log('casc', defaultRegion)
                                        } else {
                                            onFormChange({
                                                visibility_setting_location_regions: false,
                                                visibility_setting_location: 6,
                                                visible_regions: undefined,
                                                visibility_setting_location_mixed: PROXIMITY_LOCATION_SETTING,
                                            })
                                        }
                                    }}

                                    disabled={isDisabled}
                                    value={project.visibility_setting_location_mixed}
                                    defaultValue={GLOBAL_LOCATION_SETTING}
                                >
                                    <Space direction="vertical">
                                        <Radio value={GLOBAL_LOCATION_SETTING}>
                                            <div className="flex flex-row items-center gap-4">

                                                <div className="w-9 ml-2 text-center">
                                                    <i className="fas fa-globe-americas fa-2x"/>
                                                </div>

                                                <div className="flex flex-col gap-1.5">
                                                    <p className="whitespace-no-wrap text-base font-medium">
                                                        Worldwide
                                                    </p>
                                                    <p className="text-sm whitespace-no-wrap">
                                                        Talent of any location can submit proposals
                                                    </p>
                                                </div>
                                            </div>
                                        </Radio>
                                        <Radio value={REGIONAL_LOCATION_SETTING}>
                                            <div className="flex flex-row items-center gap-4">
                                                <div className="w-9 ml-2 text-center">
                                                    <i className="fas fa-flag-usa fa-2x"/>
                                                </div>
                                                <div className="flex flex-col gap-1.5">
                                                    <p className="whitespace-no-wrap text-base font-medium">
                                                        U.S. Only
                                                    </p>
                                                    <p className="text-sm whitespace-no-wrap">
                                                        Only talent in the U.S. can submit proposals
                                                    </p>
                                                </div>
                                            </div>
                                        </Radio>
                                        <label onClick={e => {
                                            if (isDisabled || project.visibility_setting_location_mixed === PROXIMITY_LOCATION_SETTING) return;

                                            return onFormChange({
                                                visibility_setting_location_regions: false,
                                                visibility_setting_location: parseInt(TB.invert(laravel.project_visibility_location_settings)[`Within ${currentMileSetting} Miles`]) || 4,
                                                visible_regions: undefined,
                                                visibility_setting_location_mixed: PROXIMITY_LOCATION_SETTING,
                                            })

                                        }} className={TB.mapClass("ant-radio-wrapper", {
                                            "clickable": project.visibility_setting_location_mixed !== PROXIMITY_LOCATION_SETTING,
                                            "ant-radio-wrapper-checked": project.visibility_setting_location_mixed === PROXIMITY_LOCATION_SETTING
                                        })}>
                                            <span className={TB.mapClass("ant-radio", {
                                                "ant-radio-checked": project.visibility_setting_location_mixed === PROXIMITY_LOCATION_SETTING
                                            })}>
                                                <span className="ant-radio-inner"/>
                                            </span>
                                            <div className="flex flex-row items-center gap-4">
                                                <div className="w-9 ml-2 text-center">
                                                    <i className="fas fa-map-marked-alt fa-2x"/>
                                                </div>
                                                <div className="flex flex-col gap-1.5">
                                                    <p className="whitespace-no-wrap text-base font-medium">
                                                        By Proximity to Location
                                                    </p>
                                                    <div
                                                        className="flex flex-row items-center justify-start gap-2.5">
                                                        <p className="m-0 whitespace-no-wrap text-sm">
                                                            Talent Lives Within
                                                        </p>
                                                        <Select
                                                            className="pointer-events-all m-0"
                                                            defaultValue={200}
                                                            value={currentMileSetting}
                                                            disabled={isDisabled || project.visibility_setting_location_mixed !== TB.locationProximitySetting()}
                                                            style={{width: 110}}
                                                            onChange={async (value) => {
                                                                console.log('alkscmlak',)
                                                                await setCurrentMileSetting(value)
                                                                return onFormChange({
                                                                    visibility_setting_location: parseInt(
                                                                        TB.invert(laravel.project_visibility_location_settings)[`Within ${value} Miles`]
                                                                    )
                                                                })
                                                            }}
                                                            options={[
                                                                {
                                                                    value: 25,
                                                                    label: '25 Miles',
                                                                },
                                                                {
                                                                    value: 50,
                                                                    label: '50 Miles',
                                                                },
                                                                {
                                                                    value: 100,
                                                                    label: '100 Miles',
                                                                },
                                                                {
                                                                    value: 200,
                                                                    label: '200 Miles',
                                                                },
                                                            ]}
                                                        />

                                                        <p className="m-0 whitespace-no-wrap text-sm">
                                                            of Project Location:
                                                        </p>
                                                        <Badge
                                                            color={project.visibility_setting_location_mixed === PROXIMITY_LOCATION_SETTING ? 'white' : TB.config('color.secondary')}
                                                            showZero
                                                            className="whitespace-no-wrap text-sm"

                                                            count={TB.truncate(project.location?.full, {length: 50})}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </label>

                                    </Space>
                                </Radio.Group>
                            </Space>
                        </div>
                        <Divider className="mt-3 mb-2"/>

                        <div className="flex flex-col items-stretch">
                            <p className="text-lg font-medium text-left text-[#3f434a] mb-3">
                                Talent Pools
                            </p>
                            {isLoading ? (
                                <div
                                    className="self-stretch min-h-[450px] flex flex-col items-center justify-center gap-2">
                                    <LoadingSpinner size="lg"/>
                                    <p className="text-sm">
                                        Loading Options
                                    </p>
                                </div>
                            ) : (
                                error ? (
                                    <div
                                        className="self-stretch min-h-[450px] flex flex-col items-center justify-center gap-2">
                                        <ErrorBanner
                                            message="Could not fetch options at this time. Please try again later"
                                            onRetry={refetch}/>
                                    </div>
                                ) : (
                                    <React.Fragment>
                                        <div>
                                            <Checkbox
                                                disabled={isDisabled}
                                                onChange={async (e) => {

                                                    if (!e.target.checked) {
                                                        onFormChange({
                                                            visibility_setting_hired: false,
                                                            visibility_setting_invited: false,
                                                            visibility_setting_favorites: false,
                                                            visible_talent_pools: [],
                                                            visibility_setting_all_talent: true
                                                        })
                                                    } else {
                                                        onFormChange({
                                                            visibility_setting_all_talent: false
                                                        })
                                                    }

                                                    await setIsSelectingTalentPoolsEnabled(e.target.checked)
                                                }}
                                                checked={isSelectingTalentPoolsEnabled}>

                                                <span className="text-base font-semibild text-left">
                                                    Only allow talent proposals from selected talent pools below
                                                </span>

                                            </Checkbox>
                                        </div>
                                        <Space
                                            className="mt-3 gap-4 items-stretch visibility-tier-1-options max-w-[650px]"
                                            direction="vertical">


                                            {TB.map(groupedPools, groupedPool => {
                                                if (!groupedPool?.pools?.length) {
                                                    return null;
                                                }
                                                if (groupedPool?.key === 'general') {
                                                    return (
                                                        <React.Fragment key={`group-${groupedPool?.key}-pools`}>
                                                            {TB.map(TB.orderBy(groupedPool.pools, pool => Math.abs(pool.id), 'asc'), generalPool => {
                                                                let generalPoolLookup = TB.find(Project.VisibilityDefaultSettings, [
                                                                    'id',
                                                                    generalPool.id
                                                                ]);
                                                                if (!generalPoolLookup) {
                                                                    return null;
                                                                }
                                                                return (
                                                                    <div className="visibility-tier-1-option"
                                                                         key={`general-pool-${generalPool.id}`}>
                                                                        <Checkbox
                                                                            disabled={isDisabled || !isSelectingTalentPoolsEnabled}
                                                                            onChange={e => onFormChange({[generalPoolLookup.key]: e.target.checked})}
                                                                            checked={project[generalPoolLookup.key]}>
                                                                            {generalPoolLookup.label}
                                                                            &nbsp;
                                                                            {generalPool?.talent_users_count ? (
                                                                                <span
                                                                                    className="text-secondary text-xs">
                                                                                    ({generalPool?.talent_users_count})
                                                                                </span>
                                                                            ) : null}
                                                                        </Checkbox>
                                                                    </div>
                                                                )
                                                            })}
                                                        </React.Fragment>
                                                    )
                                                }
                                                let selectedPools = TB.filter(project?.visible_talent_pools || [], {
                                                    'is_default': false,
                                                    'is_personal': groupedPool?.key === 'personal'
                                                });
                                                if (auth?.canManageClients() && groupedPool?.key !== 'company') {
                                                    selectedPools = TB.filter(project?.visible_talent_pools || [], pool => {
                                                        if (groupedPool?.key === 'client_users') {
                                                            return !pool.is_default && pool.is_personal && pool.owner?.id !== auth?.id
                                                        }
                                                        return !pool.is_default && pool.is_personal && pool.owner?.id === auth?.id
                                                    });
                                                }


                                                return (
                                                    <Collapse ghost className="visibility-collapse"
                                                              key={`group-${groupedPool?.key}-pools`}>
                                                        <Collapse.Panel header={(
                                                            <div className="bg-gray-100 p-4">
                                                                <Checkbox
                                                                    disabled={isDisabled || !isSelectingTalentPoolsEnabled}
                                                                    onChange={e => {

                                                                        if (e.target.checked) {
                                                                            let updatedPools = TB.uniqBy([
                                                                                ...(project.visible_talent_pools || []),
                                                                                ...groupedPool?.pools
                                                                            ], 'id');
                                                                            return onFormChange({visible_talent_pools: updatedPools})
                                                                        } else {
                                                                            let updatedPools = TB.filter(project.visible_talent_pools || [], pool => !TB.find(groupedPool?.pools, [
                                                                                'id',
                                                                                pool.id
                                                                            ]));
                                                                            return onFormChange({visible_talent_pools: updatedPools})
                                                                        }
                                                                    }}
                                                                    indeterminate={selectedPools?.length > 0 && selectedPools?.length < groupedPool?.pools?.length}
                                                                    checked={selectedPools?.length === groupedPool?.pools?.length}>
                                                                    {groupedPool?.label} Talent Pools
                                                                    &nbsp;
                                                                    {selectedPools?.length > 0 && (
                                                                        <span className="text-xs text-danger">
                                                                            ({selectedPools?.length} Selected)
                                                                        </span>
                                                                    )}
                                                                </Checkbox>
                                                            </div>
                                                        )} key={groupedPool?.key}>
                                                            <div className="ml-4 my-3 flex flex-col items-stretch">
                                                                <Space
                                                                    className="gap-4 items-stretch visibility-tier-1-options"
                                                                    direction="vertical">

                                                                    {TB.map(groupedPool?.pools, pool => {
                                                                        let isAnotherOwner = pool?.owner && auth.id !== pool?.owner?.id;
                                                                        let isChecked = TB.find(project.visible_talent_pools || [], [
                                                                            'id',
                                                                            pool.id
                                                                        ]);
                                                                        return (
                                                                            <div
                                                                                key={`group-${groupedPool?.key}-${pool.id}`}
                                                                                className="visibility-tier-1-option">
                                                                                <Checkbox
                                                                                    disabled={isDisabled || !isSelectingTalentPoolsEnabled}
                                                                                    checked={isChecked}
                                                                                    onClick={async (e) => {
                                                                                        let updatedPools = TB.xorBy(project.visible_talent_pools || [], [pool], 'id');

                                                                                        return onFormChange({visible_talent_pools: updatedPools});
                                                                                    }}
                                                                                    onChange={e => {
                                                                                        let updatedPools = TB.xorBy(project.visible_talent_pools || [], [pool], 'id');

                                                                                        return onFormChange({visible_talent_pools: updatedPools})
                                                                                    }}>
                                                                                    {pool.name}
                                                                                    &nbsp;

                                                                                    {isAnotherOwner ? (
                                                                                        <span
                                                                                            className="text-xs text-danger">
                                                                                            (Created
                                                                                            By {pool.owner?.firstNamePlusInitial()})
                                                                                        </span>
                                                                                    ) : (
                                                                                        pool.talent_users_count > 0 && (
                                                                                            <span
                                                                                                className="text-xs text-secondary">
                                                                                                ({pool.talent_users_count})
                                                                                            </span>
                                                                                        )
                                                                                    )}
                                                                                </Checkbox>
                                                                            </div>
                                                                        )
                                                                    })}

                                                                </Space>
                                                            </div>
                                                        </Collapse.Panel>
                                                    </Collapse>
                                                )
                                            })}

                                        </Space>
                                    </React.Fragment>
                                )
                            )}
                        </div>

                        {!isLoading && (
                            <React.Fragment>
                                <Divider className="mt-3 mb-2"/>
                                <div className="flex flex-col items-stretch">
                                    <div className="mb-3">
                                        <p className="text-lg font-medium text-left text-[#3f434a] mb-1">
                                            Talent Matches By Role
                                        </p>
                                    </div>

                                    <div className="self-stretch flex flex-col items-start gap-2">
                                        {project.positions?.length > 0 && TB.map(project.positions, (position, index) => {
                                            let matchedQuery = positionMatchQueries[index];
                                            if (!matchedQuery) {
                                                return null
                                            }
                                            return (
                                                <div key={`match-count-${position?.id}`}
                                                     className="flex flex-row items-center justify-between gap-4">
                                                    <p className="text-base font-medium flex-grow">
                                                        {position?.title}:
                                                    </p>
                                                    <div
                                                        className="text-primary font-semibold flex flex-row items-center gap-1">
                                                        {matchedQuery?.isLoading ? (
                                                            <span>
                                                                <span className="mr-1.5">
                                                                    <LoadingSpinner size="sm"/>
                                                                </span>
                                                                <span className="text-primary font-semibold">
                                                                    Possible Talent Matches
                                                                </span>
                                                            </span>
                                                        ) : (
                                                            <span className="font-semibold">
                                                                {matchedQuery.data?.isTotalPossible ? (
                                                                    <span>
                                                                        <span className="text-black">
                                                                            {matchedQuery.data?.matchCount}
                                                                        </span>
                                                                        <span className="text-primary font-semibold">
                                                                            &nbsp;Possible Talent Matches
                                                                        </span>
                                                                    </span>
                                                                ) : (
                                                                    <span>
                                                                        <span className="text-black">
                                                                            {matchedQuery.data?.matchCount}
                                                                        </span>
                                                                        <span className="text-primary">
                                                                            &nbsp;out of&nbsp;
                                                                        </span>
                                                                        <span className="text-black">
                                                                            {matchedQuery.data?.possibleMatchCount}
                                                                        </span>
                                                                        <span className="text-primary font-semibold">
                                                                            &nbsp;Possible Talent Matches
                                                                        </span>
                                                                    </span>
                                                                )}
                                                            </span>
                                                        )}

                                                    </div>
                                                </div>
                                            )
                                        })}
                                        {hasLowMatchCountForRole && (
                                            <div className="max-w-[450px] sm:max-w-[650px] mt-4">
                                                <Alert
                                                    message="Low number of talent matches"
                                                    description="Based upon on your project visibility settings, there is a low number of potential matches for at least one project position. You may want to broaden your search to increase the number of matches."
                                                    type="warning"
                                                    showIcon
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </React.Fragment>
                        )}

                    </div>

                </div>
            </div>

            <div className="mt-10">
                {props.showActions ? (
                    <div className="flex flex-row items-center justify-end gap-4 px-5">

                        <Button type="default" disabled={isDisabled} loading={isDisabled}
                                onClick={e => {
                                    props.onClose?.(false)
                                }}>
                            Cancel
                        </Button>
                        <Button type="primary" disabled={isDisabled || !positionMatchQueries || TB.find(positionMatchQueries, query => query?.isLoading)} loading={isDisabled}
                                ghost onClick={onUpdateSettings}>
                            Update Settings
                        </Button>
                    </div>
                ) : props.children}

            </div>
        </SpinLoader>
    )
}

export {
    ProjectVisibilitySettings
}